<script setup lang="ts">
// /financing — M9, unblocked by the open-items review (item 2).
//
// The page ships WITHOUT any specific offer: no lender, no APR, no monthly
// payment, no term, no promotional period. That is both the instruction and
// the legally safe position — advertising consumer credit terms is
// regulated (Truth in Lending / Reg Z), and any figure printed here would
// be a term Ask-HVAC.com is in no position to offer or honour.
//
// So the page does the one thing it legitimately can: teach a homeowner how
// contractor financing works and what to ask about it, then send them to the
// only party who can actually quote terms — their contractor.
//
// ANYONE EDITING THIS FILE: adding a rate, a payment, a term length or a
// lender name turns this into a credit advertisement. Don't, unless a real
// lender relationship exists and the wording has been through compliance.
import { RouterLink } from 'vue-router'

// The mechanics, explained without quoting a single term. Every one of
// these is a general description of how such products work, not an offer.
const HOW_IT_WORKS = [
    {
        title: 'The contractor arranges it, not us',
        body: 'HVAC financing is generally offered through the contractor you hire, who works with one or more lending partners. Ask-HVAC.com does not lend money, does not arrange credit, and has no role in whether an application is approved.',
    },
    {
        title: 'Programs vary from contractor to contractor',
        body: 'Two companies quoting the same system can offer completely different financing, because they work with different lenders. Available programs, interest rates, terms, qualification requirements and promotional offers all vary — there is no standard deal to describe.',
    },
    {
        title: 'Approval depends on the lender',
        body: 'The lender decides, based on their own criteria. Credit history is generally part of that decision, and applying may involve a credit check. Any question about eligibility is one for the contractor and their lender.',
    },
    {
        title: 'Terms are only real once they are in writing',
        body: 'Whatever is described verbally, the binding terms are the ones in the credit agreement you sign. Read that document before signing, and ask for a copy of anything you are agreeing to.',
    },
]

// The questions are the actual value of this page. None of them presumes
// any particular product exists.
const ASK = [
    {
        q: 'Who is the lender?',
        why: 'The contractor arranges the financing, but the loan is with a lender. You should know their name before you apply.',
    },
    {
        q: 'What is the interest rate, and is it fixed or variable?',
        why: 'A rate that can change over the life of the loan is a different product from one that cannot.',
    },
    {
        q: 'How long is the term, and what would the payment be?',
        why: 'A longer term lowers the monthly payment and usually raises the total paid. Ask for both numbers, not just the monthly one.',
    },
    {
        q: 'If there is a promotional period, what happens when it ends?',
        why: 'This is the most important question on the list. Some promotional plans charge no interest only if the full balance is cleared in time — and if it is not, interest can be applied back to the original purchase date. Ask specifically whether that is how the offer works.',
    },
    {
        q: 'Is there a penalty for paying it off early?',
        why: 'If you may be able to clear the balance ahead of schedule, you want to know whether that costs you anything.',
    },
    {
        q: 'Are there fees — origination, processing, or annual?',
        why: 'Fees change the real cost of borrowing and do not always appear in the headline figure.',
    },
    {
        q: 'Does the price change if I pay cash instead?',
        why: 'Financing is not free for the contractor to offer. It is worth knowing whether a cash price exists and what it is.',
    },
]

const ALTERNATIVES = [
    {
        title: 'Compare more than one quote',
        body: 'Financing is easier to judge when you know what the work should cost. Two or three quotes on the same scope is the single most useful thing you can do before borrowing anything.',
    },
    {
        title: 'Check your own bank or credit union',
        body: 'A home improvement loan or line of credit arranged independently is worth comparing against whatever the contractor offers. Sometimes it is better, sometimes not — you will not know without looking.',
    },
    {
        title: 'Look for utility and manufacturer programs',
        body: 'Rebates, efficiency incentives and manufacturer promotions come and go, and they reduce the amount you need to finance in the first place. Ask your contractor what currently applies to the equipment they are quoting.',
    },
    {
        title: 'Ask whether the work can be staged',
        body: 'Not every quote has to be done at once. If part of it is an upgrade rather than a necessity, ask what happens if you defer that piece.',
    },
]
</script>

<template>
    <section class="hero">
        <div class="hero-inner">
            <p class="crumb">Home <span>›</span> Financing</p>
            <h1>Financing a <span class="hl">New HVAC System</span></h1>
            <p class="lede">
                A replacement is one of the larger purchases a homeowner makes, and many people
                finance it. Here is how contractor financing generally works and what to ask about
                it — so the conversation happens on your terms rather than at the end of a long
                sales appointment.
            </p>
        </div>
    </section>

    <!-- The disclosure sits above the content, not buried under it: this is
         the single most important thing on the page. -->
    <section class="notice">
        <div class="notice-inner">
            <span class="mark" aria-hidden="true">!</span>
            <div>
                <strong>Ask-HVAC.com does not provide financing.</strong>
                <p>
                    We are not a lender, a broker or a credit intermediary. We do not offer credit,
                    arrange it, or determine any financing terms, and nothing on this page is an
                    offer of credit or a recommendation of any lender or financing product.
                    Financing options may be available through the HVAC contractor you choose.
                    Programs, rates, terms, qualification requirements and lenders vary from
                    contractor to contractor — discuss available financing and exact terms directly
                    with your contractor.
                </p>
            </div>
        </div>
    </section>

    <section class="how">
        <div class="how-inner">
            <h2>How HVAC financing generally works</h2>
            <p class="sub">
                Described in general terms, because that is all anyone can honestly say without
                knowing which contractor you hire.
            </p>
            <div class="how-grid">
                <div v-for="h in HOW_IT_WORKS" :key="h.title" class="card">
                    <strong>{{ h.title }}</strong>
                    <p>{{ h.body }}</p>
                </div>
            </div>
        </div>
    </section>

    <section class="ask">
        <div class="ask-inner">
            <div class="ask-lede">
                <h2>What to ask your contractor</h2>
                <p class="sub">
                    Take this list into the appointment. A contractor who is comfortable with their
                    financing offer will answer every one of these without difficulty.
                </p>
                <p class="fine">
                    None of these questions can be answered here — the answers depend entirely on
                    the contractor and the lender they work with.
                </p>
            </div>

            <ol class="qlist">
                <li v-for="a in ASK" :key="a.q">
                    <strong>{{ a.q }}</strong>
                    <p>{{ a.why }}</p>
                </li>
            </ol>
        </div>
    </section>

    <section class="alts">
        <div class="alts-inner">
            <h2>Before you finance anything</h2>
            <p class="sub">
                Financing is a way to pay for the work. It is not a reason to do more work than the
                house needs, and a lower monthly payment on an oversized quote is still an
                oversized quote.
            </p>
            <div class="alts-grid">
                <div v-for="a in ALTERNATIVES" :key="a.title" class="card">
                    <strong>{{ a.title }}</strong>
                    <p>{{ a.body }}</p>
                </div>
            </div>
        </div>
    </section>

    <section class="closing">
        <div class="closing-inner">
            <h2>Work out what you actually need first</h2>
            <p>
                The cheapest financing on the wrong scope of work is still the wrong scope of work.
                Check whether replacement is the right call, get a sense of the range, then talk
                terms.
            </p>
            <div class="actions">
                <RouterLink class="cta" to="/repair-or-replace">
                    Should I repair or replace? →
                </RouterLink>
                <RouterLink class="cta cta--ghost" to="/cost-guides">See cost ranges</RouterLink>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    background: var(--site-gradient-navy);
    color: #fff;
}

.hero-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.7rem, 3.3vw, 2.5rem);
    font-weight: 800;
    line-height: 1.16;
    margin: 0 0 var(--site-space-md);
}

.hl {
    color: var(--site-accent);
}

.lede {
    max-width: 42rem;
    margin: 0;
    color: rgba(255, 255, 255, 0.86);
    line-height: 1.65;
}

/* ---- the disclosure ---- */
.notice {
    background: var(--site-accent-soft);
    border-bottom: 1px solid var(--site-border);
}

.notice-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-lg);
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        color: var(--site-navy);
        margin-bottom: 0.25rem;
    }

    p {
        margin: 0;
        max-width: 62rem;
        font-size: 0.86rem;
        line-height: 1.65;
        color: var(--site-text-muted);
    }
}

.mark {
    display: grid;
    place-items: center;
    width: 1.7rem;
    height: 1.7rem;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--site-accent);
    color: #fff;
    font-family: var(--site-font-heading);
    font-weight: 800;
    font-size: 0.95rem;
}

/* ---- shared ---- */
h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 0.4rem;
}

.sub {
    margin: 0 0 var(--site-space-lg);
    max-width: 46rem;
    color: var(--site-text-muted);
    font-size: 0.92rem;
    line-height: 1.6;
}

.how-inner,
.ask-inner,
.alts-inner,
.closing-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

.card {
    padding: var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        color: var(--site-navy);
        margin-bottom: 0.3rem;
    }

    p {
        margin: 0;
        font-size: 0.85rem;
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

/* ---- how ---- */
.how-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
    gap: var(--site-space-md);
}

/* ---- ask ---- */
.ask {
    background: var(--site-bg-alt);
}

.ask-inner {
    display: grid;
    grid-template-columns: 21rem minmax(0, 1fr);
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
}

.fine {
    margin: 0;
    padding: 0.7rem 0.9rem;
    border-left: 3px solid var(--site-accent);
    background: var(--site-surface);
    border-radius: var(--site-radius-sm);
    font-size: 0.83rem;
    line-height: 1.55;
    color: var(--site-text-muted);
}

.qlist {
    margin: 0;
    padding-left: 1.3rem;
    display: grid;
    gap: var(--site-space-md);

    li {
        padding-left: 0.3rem;

        &::marker {
            color: var(--site-accent);
            font-weight: 700;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        color: var(--site-navy);
        margin-bottom: 0.15rem;
    }

    p {
        margin: 0;
        font-size: 0.85rem;
        line-height: 1.6;
        color: var(--site-text-muted);
    }
}

/* ---- alternatives ---- */
.alts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
    gap: var(--site-space-md);
}

/* ---- closing ---- */
.closing {
    background: var(--site-bg-alt);
}

.closing-inner {
    text-align: center;
    max-width: 44rem;

    p {
        margin: 0 auto var(--site-space-lg);
        max-width: 34rem;
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

.actions {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--site-space-sm);
}

.cta {
    padding: 0.75rem 1.4rem;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.92rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }

    &--ghost {
        background: none;
        border: 1px solid var(--site-border);
        color: var(--site-navy);

        &:hover {
            background: none;
            border-color: var(--site-blue);
            color: var(--site-blue);
        }
    }
}
</style>
